import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Settings, Save, Bell, Mail, Clock, Globe2, Link } from "lucide-react";

export default function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    authAxios.get("/analytics/command/settings")
      .then(res => setSettings(res.data))
      .catch(console.error);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await authAxios.put("/analytics/command/settings", {
        daily_pulse_email: settings.daily_pulse_email,
        daily_pulse_timezone: settings.daily_pulse_timezone,
        daily_pulse_enabled: settings.daily_pulse_enabled,
        anomaly_alerts_enabled: settings.anomaly_alerts_enabled,
        anomaly_alert_email: settings.anomaly_alert_email,
        anomaly_webhook_url: settings.anomaly_webhook_url,
      });
      setSettings(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (!settings) return <div className="flex items-center justify-center h-64 text-white/20">Loading settings...</div>;

  return (
    <div className="space-y-6">
      {/* ── Daily Pulse ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="settings-daily-pulse">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Daily Pulse Email
        </h2>
        <p className="text-xs text-white/30 mb-4">
          Cron job sends a daily briefing at 9:00 AM in your configured timezone. Updates schedule when timezone changes.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.daily_pulse_enabled || false}
                onChange={e => updateField("daily_pulse_enabled", e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-[#0a0a0a] accent-[#C5A55A]"
                data-testid="pulse-enabled-toggle"
              />
              <span className="text-sm text-white/70">Enable Daily Pulse</span>
            </label>
          </div>

          <div>
            <label className="text-xs text-white/40 mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" /> Recipient Email</label>
            <input
              type="text"
              value={settings.daily_pulse_email || ""}
              onChange={e => updateField("daily_pulse_email", e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#C5A55A]/50"
              data-testid="pulse-email-input"
            />
          </div>

          <div>
            <label className="text-xs text-white/40 mb-1 block flex items-center gap-1"><Globe2 className="w-3 h-3" /> Timezone</label>
            <select
              value={settings.daily_pulse_timezone || "Australia/Sydney"}
              onChange={e => updateField("daily_pulse_timezone", e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#C5A55A]/50"
              data-testid="pulse-timezone-select"
            >
              {(settings.available_timezones || []).map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ── Anomaly Alerts ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="settings-anomaly-alerts">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4" /> Anomaly Alerts
        </h2>
        <p className="text-xs text-white/30 mb-4">
          Monitors 6 alert rules every 5 minutes + 3-hour cron safety net. CRITICAL/HIGH alerts send immediate emails.
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.anomaly_alerts_enabled || false}
                onChange={e => updateField("anomaly_alerts_enabled", e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-[#0a0a0a] accent-[#C5A55A]"
                data-testid="anomaly-enabled-toggle"
              />
              <span className="text-sm text-white/70">Enable Anomaly Monitoring</span>
            </label>
          </div>

          <div>
            <label className="text-xs text-white/40 mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" /> Alert Email</label>
            <input
              type="text"
              value={settings.anomaly_alert_email || ""}
              onChange={e => updateField("anomaly_alert_email", e.target.value)}
              placeholder="alerts@domain.com, ops@domain.com"
              className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#C5A55A]/50"
              data-testid="anomaly-email-input"
            />
            <p className="text-[10px] text-white/20 mt-1">Comma-separated for multiple recipients</p>
          </div>

          <div>
            <label className="text-xs text-white/40 mb-1 block flex items-center gap-1"><Link className="w-3 h-3" /> Webhook URL (future Slack)</label>
            <input
              type="url"
              value={settings.anomaly_webhook_url || ""}
              onChange={e => updateField("anomaly_webhook_url", e.target.value)}
              placeholder="https://hooks.slack.com/..."
              className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#C5A55A]/50"
              data-testid="anomaly-webhook-input"
            />
          </div>
        </div>
      </section>

      {/* ── Save Button ── */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
            saved ? "bg-emerald-500/20 text-emerald-400" : "bg-[#C5A55A]/20 text-[#C5A55A] hover:bg-[#C5A55A]/30"
          } disabled:opacity-50`}
          data-testid="save-settings-btn"
        >
          {saved ? <><Save className="w-4 h-4" /> Saved</> :
           saving ? "Saving..." :
           <><Save className="w-4 h-4" /> Save Settings</>}
        </button>
      </div>
    </div>
  );
}
